import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { Typography } from "@mui/material";
import { AppTable, ExportDialog, ImportExportActions } from "@/components/common";
import { PaginationType } from "@/types/common";
import { ResponseRequestType } from "@/types/service";
import { AppConstant, KeyConstant, LangConstant } from "@/const";
import {
  InternalRequestFilter,
  useImportRequest,
  useLoading,
  useResponseColumn,
  useRequestList,
  useSnackbar,
} from "@/hooks";
import { MainBodyPage, TableContainer } from "./ViolationResponseList.style";
import { ViolationResponseImportDialog } from "./components";
import { isElectron, uuid } from "@/utils/common.utils";
import useViolationResponseList, { InternalResponseFilter } from "./hooks/useViolationResponseList";
import { RESPONSE_TYPE, STATUS_REQUEST } from "@/const/system.const";
import ResponseRequestFilter from "../IdentityResponseList/components/ResponseRequestFilter";
import LoadingEncryptDialog from "@/components/common/dialog/LoadingEncryptDialog";
import DialogDetailResponse from "@/components/common/dialog/DialogDetailResponse";
import useSendNoticeToDatalake from "@/hooks/useSendNoticeToDatalake";
import { useDataContext } from "@/hooks/useDataContext";
import { DEFAULT_PAGINATION } from "@/const/app.const";

const DEFAULT_FILTER: InternalResponseFilter = {
  applicationSendUid: AppConstant.SELECT_ALL,
  status: AppConstant.SELECT_ALL,
};

const ViolationResponseListPage = () => {
  const { t: getLabel } = useTranslation(LangConstant.NS_REQUEST);
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { openLoading } = useLoading();

  const [pageData, setPageData] = useState<Partial<PaginationType>>({});
  const [filter, setFilter] = useState<InternalResponseFilter>(DEFAULT_FILTER);
  const [filesUpload, setFilesUpload] = useState<File[]>([]);
  const [openUpload, setOpenUpload] = useState<boolean>(false);
  const [isOpenExportDialog, setIsOpenExportDialog] = useState<boolean>(false);
  const [selectedRequests, setSelectedRequests] = useState<any[]>([]);
  const [dataSelected, setDataSelected] = useState<any>(null);
  const { violationResponseList, setViolationResponseList } = useDataContext();

  const { mutate: sendNoticeToDatalake } = useSendNoticeToDatalake();
  const { importRequest } = useImportRequest("queryResponse");
  const { columnList } = useResponseColumn({ setDataSelected: setDataSelected });
  const { requestList, pagination } = useViolationResponseList({
    filterParams: filter,
    typeParam: RESPONSE_TYPE.ship,
    paginationParam: pageData,
  });

  const dataRender = requestList.map((item: ResponseRequestType) => {
    return {
      ...item,
      uid: item.transferResponseUid,
    };
  });

  const onFileUpload = (files: File[]) => {
    setFilesUpload(files);
    setOpenUpload(true);
  };

  const handleSelectedRequest = (uidList: string[]) => {
    setSelectedRequests(dataRender.filter((request: ResponseRequestType) => uidList.includes(request.uid)));
  };

  const handleChangeFilter = (filter: InternalResponseFilter) => {
    setFilter(filter);
    setPageData(DEFAULT_PAGINATION);
  };

  const handleImportSuccess = () => {
    openLoading(false);
    setOpenUpload(false);
    openSnackbar(getLabel("MSG_UPLOAD_SUCCESS"), "success");
    queryClient.invalidateQueries({
      queryKey: [KeyConstant.REACT_QUERY.getInternalRequest],
    });
  };

  const handleImportError = () => {
    openSnackbar(getLabel("MSG_UPLOAD_FAILURE"), "error");
    openLoading(false);
  };

  const mutationImport = useMutation({
    mutationFn: (file: File) => importRequest(file),
    onSuccess: () => {
      handleImportSuccess();
    },
    onError: () => {
      handleImportSuccess();
    },
  });

  const onImport = () => {
    openLoading(true);
    mutationImport.mutate(filesUpload[0]);
  };

  const handleExport = () => {
    setIsOpenExportDialog(true);
  };

  const handleCloseExportDialog = () => {
    setIsOpenExportDialog(false);
  };

  const handleStartEncrypt = () => {
    openLoading(true);
  };

  const handleEncryptCompleted = async (file: ArrayBuffer) => {
    openLoading(false);
    handleCloseExportDialog();
    const blob = new Blob([file], { type: "application/zip" });
    const aTag = document.createElement("a");
    const url = URL.createObjectURL(blob);
    aTag.href = url;
    aTag.download = "request";
    aTag.click();
    aTag.remove();
  };

  const handleEncryptFailure = () => {
    openLoading(false);
    handleCloseExportDialog();
    openSnackbar(getLabel("MSG_ERROR", { ns: LangConstant.NS_COMMON }), "error");
  };

  const handleCloseDetails = () => {
    setDataSelected(null);
  };

  const handleReject = () => {
    let index = violationResponseList.findIndex(item => item.uid == dataSelected.data.uid);
    let newData = violationResponseList;
    newData[index] = {
      ...violationResponseList[index],
      status: STATUS_REQUEST.rejected,
    };
    setDataSelected(null);
    setViolationResponseList(newData);
  };

  const handleAccept = () => {
    let index = violationResponseList.findIndex(item => item.uid == dataSelected.data.uid);
    let newData = violationResponseList;
    newData[index] = {
      ...violationResponseList[index],
      status: STATUS_REQUEST.resolved,
    };
    setDataSelected(null);
    setViolationResponseList(newData);
    sendNoticeToDatalake();
  };

  return (
    <MainBodyPage>
      <Typography variant="h4" gutterBottom>
        {getLabel("L_VIOLATION_RESPONSE_TITLE")}
      </Typography>
      <ResponseRequestFilter onChangeFilter={handleChangeFilter} filter={filter} />
      {isElectron() && <ImportExportActions onExport={handleExport} onUpload={onFileUpload} />}
      <TableContainer>
        <AppTable
          data={dataRender}
          columns={columnList}
          pagination={pagination}
          onChangePagination={setPageData}
          onSelectedRequest={handleSelectedRequest}
        />
      </TableContainer>
      <ExportDialog
        open={isOpenExportDialog}
        mode="queryResponse"
        requests={selectedRequests}
        onClose={handleCloseExportDialog}
        onStart={handleStartEncrypt}
        onSuccess={handleEncryptCompleted}
        onFailure={handleEncryptFailure}
      />
      <ViolationResponseImportDialog
        onImport={onImport}
        filesUpload={filesUpload}
        openUpload={openUpload}
        onChangeOpenUpload={setOpenUpload}
      />
      {dataSelected?.mode == "view" && (
        <DialogDetailResponse
          open={true}
          data={dataSelected.data}
          onClose={handleCloseDetails}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}
    </MainBodyPage>
  );
};
export default ViolationResponseListPage;
